// The loaders should preferably not be async, but this is a special case because we need the billing context to render any of the children

import {
  Outlet,
  useLoaderData,
  useRevalidator,
  type LoaderFunctionArgs,
} from "react-router"
import { useEffect, useRef } from "react"
import { resolveContext } from "~/server/context.server"
import { isDocsPath } from "~/lib/consts"
import { useAuth } from "~/contexts/AuthContext"
import { AppFeature } from "~/server/models/appfeature"
import { redirect } from "react-router"
import ConsoleLayout from "~/components/ConsoleLayout"
import Pine59WelcomeDialog from "~/components/Pine59WelcomeDialog"
import { ErrorBoundary as ErrorBoundaryComponent } from "~/components/Errors"
import type { BillingContextCookie } from "~/server/cookies.server"
import { hasPublicDocsLinkFeature } from "~/server/posthog.server"

// Please do not fetch and await for anything in this loader
export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  // Docs live under a secret prefix `/<key>/docs/...` (and the legacy `/docs`
  // path). Soft-auth applies there so logged-out visitors can still reach them.
  const isDocs = isDocsPath(url.pathname)

  // Docs are publicly accessible — soft-auth: if the visitor has no usable
  // session we still render the docs shell with a logged-out nav. An expired
  // session is not "logged out": re-throw the refresh-token redirect so the
  // client can renew the token and come back (CAT-1189).
  const resolved = await resolveContext(request)
  if (!resolved.ctx) {
    if (!isDocs || resolved.reason === "expired") {
      throw resolved.response
    }
    return {
      billingContext: {} as BillingContextCookie,
      roles: [] as string[],
      hasOperatingOrgAccess: false,
      userAccountCount: 0,
      authed: false,
      userEmail: undefined as string | undefined,
      showDocsLink: false as boolean | Promise<boolean>,
    }
  }
  const ctx = resolved.ctx

  if (!ctx.billingContext.id) {
    if (isDocs) {
      // Authed but no account selected — still allow viewing docs.
      return {
        billingContext: ctx.billingContext,
        roles: ctx.roles ?? [],
        hasOperatingOrgAccess: ctx.hasOperatingOrgAccess,
        userAccountCount: ctx.userAccountCount,
        authed: true,
        userEmail: ctx.userEmail,
        showDocsLink: false as boolean | Promise<boolean>,
      }
    }
    console.log("redirecting to select-account")
    throw redirect(
      `/select-account?redirectTo=${url.pathname}`,
    )
  }

  if (!ctx.billingContext.enabledFeatures?.includes(AppFeature.CONSOLE)) {
    if (isDocs) {
      return {
        billingContext: ctx.billingContext,
        roles: ctx.roles ?? [],
        hasOperatingOrgAccess: ctx.hasOperatingOrgAccess,
        userAccountCount: ctx.userAccountCount,
        authed: true,
        userEmail: ctx.userEmail,
        showDocsLink: false as boolean | Promise<boolean>,
      }
    }
    const accountName = ctx.billingContext.displayName
    throw redirect(`/no-access?reason=no-console-access${accountName ? `&account=${encodeURIComponent(accountName)}` : ""}`)
  }

  return {
    billingContext: ctx.billingContext,
    roles: ctx.roles ?? [],
    hasOperatingOrgAccess: ctx.hasOperatingOrgAccess,
    userAccountCount: ctx.userAccountCount,
    authed: true,
    userEmail: ctx.userEmail,
    // Deferred (not awaited) so it never blocks this hot layout loader —
    // resolved with <Await> in the sidebar. See _console.accounts.tsx.
    showDocsLink: hasPublicDocsLinkFeature(
      ctx.userId,
      ctx.billingContext.id,
    ) as boolean | Promise<boolean>,
  }
}

/**
 * Docs must server-render for visitors without a session (crawlers, the
 * unacast.com redirect), so a docs deep link can't go through the refresh page
 * the way the console root does. That means the server renders the logged-out
 * shell while the browser may still hold a Firebase session (cookie cleared, or
 * past its 30-day maxAge). AuthProvider syncs a fresh token on load; once that
 * has settled, re-run the loaders once so the nav reflects the real state
 * instead of waiting for the next navigation (CAT-1189).
 */
function useRevalidateOnceSessionSynced(authed: boolean) {
  const { user, isLoading } = useAuth()
  const revalidator = useRevalidator()
  const done = useRef(false)
  useEffect(() => {
    if (authed || isLoading || !user?.emailVerified || done.current) return
    done.current = true
    revalidator.revalidate()
  }, [authed, isLoading, user, revalidator])
}

export default function ConsoleIndex() {
  const {
    billingContext,
    roles,
    hasOperatingOrgAccess,
    userAccountCount,
    authed,
    showDocsLink,
  } = useLoaderData<typeof loader>()
  useRevalidateOnceSessionSynced(authed)

  return (
    <ConsoleLayout
      billingContext={billingContext}
      roles={roles}
      hasOperatingOrgAccess={hasOperatingOrgAccess}
      userAccountCount={userAccountCount}
      authed={authed}
      showDocsLink={showDocsLink}
    >
      {/* Not gated on auth: the unacast.com → console.pine59.com/docs redirect
          lands logged-out visitors here, and they need the rebrand context most. */}
      <Pine59WelcomeDialog />
      <Outlet context={{ billingContext, authed }} />
    </ConsoleLayout>
  )
}

export function ErrorBoundary() {
  return <ErrorBoundaryComponent />
}
